<script setup lang="ts">
import InstallSnippetStep from '@/components/settings/InstallSnippetStep.vue';
import VerifyConnectionStep from '@/components/settings/VerifyConnectionStep.vue';
import SetupDoneStep from '@/components/settings/SetupDoneStep.vue';
import type { InstallationTestStatus } from '@/domain/installation-guide';
import { computed, onBeforeUnmount, ref } from "vue";
import { ChevronLeft, ChevronRight } from "lucide-vue-next";
import { apiFetch } from "@/services/api";
import { t } from "@/i18n";
import { connectorInstallBlock, createInstallationGuide } from "@/domain/installation-guide";
import Button from "@/volt/Button.vue";

export type OnboardingResult = {
  tenant: { id: string; name: string };
  site: { id: string; name: string; primaryDomain: string; environment: string; stack: string; publicTagId: string };
  privacyTemplates: { version: string; de: string; en: string };
  documents: { consentPolicyVersion: string };
};

const props = defineProps<{ result: OnboardingResult }>();
const emit = defineEmits<{ complete: [] }>();
// Same steps as the site wizard after a website exists: install, verify, done.
const steps = ["Einbauen", "Prüfen", "Fertig"];
const step = ref(0);
const testStatus = ref<InstallationTestStatus>();
const testVisitor = ref(false);
const error = ref("");
const saving = ref(false);
let disposed = false;
const installationGuide = computed(() => createInstallationGuide({
  origin: window.location.origin,
  siteId: props.result.site.id,
  publicTagId: props.result.site.publicTagId,
  primaryDomain: props.result.site.primaryDomain,
  policyVersion: props.result.documents.consentPolicyVersion
}));
const connectorSnippet = computed(() => connectorInstallBlock(installationGuide.value));

async function finish() {
  if (saving.value) return;
  saving.value = true; error.value = '';
  try {
    await apiFetch(`/admin/sites/${encodeURIComponent(props.result.site.id)}/setup`, {
      method: 'PATCH',
      body: JSON.stringify({ desiredModes: testVisitor.value ? ['account', 'visitor'] : ['account'], setupStep: 'complete', setupStatus: testStatus.value?.status === 'passed' ? 'ready' : 'verification_pending' })
    });
    if (!disposed) emit('complete');
  } catch (reason) { error.value = reason instanceof Error ? reason.message : 'Einrichtung konnte nicht gespeichert werden.'; }
  finally { saving.value = false; }
}
onBeforeUnmount(() => { disposed = true; });
</script>

<template>
  <div class="first-run-page site-wizard-backdrop">
    <section class="site-wizard first-run-card setup-flow" aria-labelledby="first-run-title">
      <header class="site-wizard__header">
        <div><h2 id="first-run-title">{{ t('setup.title') }}</h2><p>{{ result.site.name }} · {{ result.site.primaryDomain }}</p></div>
      </header>
      <ol class="site-wizard__steps setup-progress" :style="{ '--setup-steps': steps.length }">
        <li v-for="(label, index) in steps" :key="label" :class="{ active: step === index, done: step > index }" :aria-current="step === index ? 'step' : undefined"><span>{{ step > index ? '✓' : index + 1 }}</span><small>{{ label }}</small></li>
      </ol>

      <main class="site-wizard__body">
        <InstallSnippetStep v-if="step === 0" :snippet="connectorSnippet" :policy-version="result.documents.consentPolicyVersion" :privacy-templates="result.privacyTemplates" />
        <!-- Kept mounted so a running test keeps polling while the user moves between steps. -->
        <VerifyConnectionStep v-show="step === 1" v-model:include-consent="testVisitor" v-model:status="testStatus" :site-id="result.site.id" :domain="result.site.primaryDomain" />
        <SetupDoneStep v-if="step === 2" :status="testStatus" :include-consent="testVisitor" />
        <p v-if="error" class="auth-error setup-flow__error" role="alert">{{ error }}</p>
      </main>

      <footer class="site-wizard__footer">
        <Button variant="ghost" :disabled="step === 0 || saving" @click="step--"><ChevronLeft :size="14" /> {{ t('common.back') }}</Button>
        <Button v-if="step < steps.length - 1" variant="primary" @click="step++">{{ t('common.next') }} <ChevronRight :size="14" /></Button>
        <Button v-else variant="primary" :disabled="saving" @click="finish">{{ t('setup.openWorkspace') }}</Button>
      </footer>
    </section>
  </div>
</template>
